import React, { useState, useEffect, useContext } from "react"
import { useNavigate } from "react-router-dom"
import { SessionContext } from "context/sessionContext"
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  Table,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
  Badge,
  Spinner,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap"
import classnames from "classnames"
import Swal from "sweetalert2"
import Breadcrumb from "components/Common/Breadcrumb"
import { sessionApi } from "apis/SessionApi"
import { promotionRequestsApi } from "apis/PromotionRequestsApi"
import Select from "react-select"
import { classApi } from "apis/ClassListApi"

const PromotionRequests = () => {
  document.title = "Promotion Requests"

  const navigate = useNavigate()

  // Tabs: "Pending", "Approved", "Rejected"
  // Map "Pending" tab to the API status "Pending Admin Approval"
  const [activeTab, setActiveTab] = useState("Pending")
  const [requests, setRequests] = useState([])
  const [loading, setLoading] = useState(false)

  const { Session } = useContext(SessionContext)
  const activeSessionId = Session || sessionStorage.getItem("SessionId")

  // Search & Filter
  const [searchQuery, setSearchQuery] = useState("")
  const [appliedSearch, setAppliedSearch] = useState("")
  const [selectedClass, setSelectedClass] = useState(null)

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)

  // Counts for badge notifications (Pending, Approved, Rejected)
  const [counts, setCounts] = useState({ Pending: 0, Approved: 0, Rejected: 0 })

  // Class list
  const [classList, setClassList] = useState([])
  const [classListError, setClassListError] = useState("")

  // Modals state
  const [approveModal, setApproveModal] = useState(false)
  const [rejectModal, setRejectModal] = useState(false)
  const [selectedRequest, setSelectedRequest] = useState(null)
  const [rejectionReason, setRejectionReason] = useState("")

  const [isExporting, setIsExporting] = useState(false)
  const [exportDropdownOpen, setExportDropdownOpen] = useState(false)

  // ── Fetch class list ─────────────────────────────────────────────────────
  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const res = await classApi.getAllClass()
        if (res?.data?.section_list && Array.isArray(res.data.section_list)) {
          const unique = res.data.section_list
            .map(cl => ({
              value: cl.UID,
              label: `${cl.st_class.toUpperCase()} / ${cl.st_sec.toUpperCase()}`,
              st_class: cl.st_class,
              st_sec: cl.st_sec,
            }))
            .filter(
              (item, index, arr) =>
                index === arr.findIndex(d => d.label === item.label)
            )
          setClassList(unique)
        } else {
          setClassListError("Class list could not be loaded.")
        }
      } catch (err) {
        console.error("Failed to fetch class list:", err.message)
        setClassListError(
          "Failed to load classes. Please refresh and try again."
        )
      }
    }
    fetchClasses()
  }, [])

  const selectStyles = {
    menu: provided => ({ ...provided, backgroundColor: "#fff", zIndex: 1000 }),
    menuList: provided => ({
      ...provided,
      backgroundColor: "#fff",
      maxHeight: "200px",
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? "#f8f9fa" : "#fff",
      color: "#000",
      cursor: "pointer",
    }),
  }

  // Fetch count stats for the badges
  const fetchCounts = async () => {
    if (!activeSessionId) return
    try {
      const statuses = {
        Pending: "Pending Admin Approval",
        Approved: "Promoted",
        Rejected: "Rejected"
      }
      const updatedCounts = { Pending: 0, Approved: 0, Rejected: 0 }
      for (const [key, apiStatus] of Object.entries(statuses)) {
        const res = await promotionRequestsApi.getRequests({
          session_id: parseInt(activeSessionId, 10),
          status: apiStatus,
          page: 1,
          page_size: 1,
          st_class: selectedClass ? selectedClass.st_class : undefined,
          st_sec: selectedClass ? selectedClass.st_sec : undefined,
        })
        if (res?.data?.status) {
          updatedCounts[key] = res.data.pagination?.total_records ?? 0
        }
      }
      setCounts(updatedCounts)
    } catch (err) {
      console.error("Failed to fetch request counts:", err.message)
    }
  }

  // Fetch main list of requests
  const fetchRequests = async (page = 1, search = "") => {
    if (!activeSessionId) return
    setLoading(true)

    const apiStatus = activeTab === "Pending" ? "Pending Admin Approval" : activeTab === "Approved" ? "Promoted" : activeTab

    try {
      const res = await promotionRequestsApi.getRequests({
        session_id: parseInt(activeSessionId, 10),
        status: apiStatus,
        search,
        page,
        page_size: 10,
        st_class: selectedClass ? selectedClass.st_class : undefined,
        st_sec: selectedClass ? selectedClass.st_sec : undefined,
      })

      if (res?.data?.status) {
        setRequests(res.data.requests || [])
        setCurrentPage(res.data.pagination?.current_page ?? page)
        setTotalPages(res.data.pagination?.total_pages ?? 1)
        setTotalCount(res.data.pagination?.total_records ?? 0)
      } else {
        setRequests([])
        setTotalPages(1)
        setTotalCount(0)
      }
    } catch (err) {
      console.error("Failed to fetch promotion requests:", err.message)
      setRequests([])
    } finally {
      setLoading(false)
    }
  }

  // Reset selection when session changes
  useEffect(() => {
    setSelectedClass(null)
    setSearchQuery("")
    setAppliedSearch("")
    setCurrentPage(1)
  }, [activeSessionId])

  // Reload when session, tab, search or selectedClass changes
  useEffect(() => {
    if (activeSessionId) {
      fetchRequests(1, appliedSearch)
      fetchCounts()
    }
  }, [activeSessionId, activeTab, appliedSearch, selectedClass])

  // Search Debouncing
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setAppliedSearch(searchQuery)
    }, 500)
    return () => clearTimeout(delayDebounce)
  }, [searchQuery])

  // Pagination Change
  const handlePageChange = page => {
    if (page < 1 || page > totalPages) return
    fetchRequests(page, appliedSearch)
  }

  // Approve action handlers
  const openApproveModal = req => {
    setSelectedRequest(req)
    setApproveModal(true)
  }

  const handleApprove = async () => {
    if (!selectedRequest) return
    try {
      const res = await promotionRequestsApi.performAction({
        promotion_id: selectedRequest.id,
        action: "Promote",
        admin_remark: "Approved by Admin",
      })

      if (res?.data?.status) {
        setApproveModal(false)
        setSelectedRequest(null)
        Swal.fire({
          title: "Promoted!",
          text: "Student has been successfully promoted.",
          icon: "success",
          confirmButtonColor: "#556ee6",
        })
        fetchRequests(currentPage, appliedSearch)
        fetchCounts()
      } else {
        Swal.fire("Error", res?.data?.message || "Failed to promote student.", "error")
      }
    } catch (err) {
      console.error("Approve action failed:", err.message)
      Swal.fire("Error", "Failed to process promotion. Please try again.", "error")
    }
  }

  // Reject action handlers
  const openRejectModal = req => {
    setSelectedRequest(req)
    setRejectionReason("")
    setRejectModal(true)
  }

  const handleReject = async () => {
    if (!selectedRequest) return
    try {
      const res = await promotionRequestsApi.performAction({
        promotion_id: selectedRequest.id,
        action: "Reject",
        admin_remark: rejectionReason || "Rejected by Admin",
      })

      if (res?.data?.status) {
        setRejectModal(false)
        setSelectedRequest(null)
        Swal.fire({
          title: "Rejected!",
          text: res.data.message || "Promotion request has been rejected.",
          icon: "warning",
          confirmButtonColor: "#f46a6a",
        })
        fetchRequests(currentPage, appliedSearch)
        fetchCounts()
      } else {
        Swal.fire("Error", res?.data?.message || "Failed to reject promotion request.", "error")
      }
    } catch (err) {
      console.error("Reject action failed:", err.message)
      Swal.fire("Error", "Failed to process promotion rejection. Please try again.", "error")
    }
  }

  const handleExport = async format => {
    if (!activeSessionId) return
    setIsExporting(true)
    const apiStatus = activeTab === "Pending" ? "Pending Admin Approval" : activeTab === "Approved" ? "Promoted" : activeTab

    try {
      const res = await promotionRequestsApi.exportRequests({
        session_id: parseInt(activeSessionId, 10),
        status: apiStatus,
        search: appliedSearch,
        export_format: format,
        st_class: selectedClass ? selectedClass.st_class : undefined,
        st_sec: selectedClass ? selectedClass.st_sec : undefined,
      })

      // If the response is JSON (usually an error message), don't download it
      if (res.data && res.data.type === "application/json") {
        const text = res.data.text ? await res.data.text() : await new Promise(r => {
          const reader = new FileReader()
          reader.onload = () => r(reader.result)
          reader.readAsText(res.data)
        })
        try {
          const errorJson = JSON.parse(text)
          Swal.fire("Error", errorJson.msg || "Failed to export report.", "error")
        } catch (e) {
          Swal.fire("Error", "Failed to export report.", "error")
        }
        return
      }

      const fileType =
        format === "excel"
          ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          : "application/pdf"
      const fileExtension = format === "excel" ? "xlsx" : "pdf"

      const blob = new Blob([res.data], { type: fileType })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", `Promotion_Requests_Report_${activeTab}.${fileExtension}`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error("Export failed:", err)
      if (
        err.response?.data &&
        err.response.data instanceof Blob &&
        err.response.data.type === "application/json"
      ) {
        try {
          const text = err.response.data.text
            ? await err.response.data.text()
            : await new Promise(r => {
                const reader = new FileReader()
                reader.onload = () => r(reader.result)
                reader.readAsText(err.response.data)
              })
          const errorJson = JSON.parse(text)
          Swal.fire("Error", errorJson.msg || "Failed to export report.", "error")
          return
        } catch (e) {
          // fallback
        }
      }
      Swal.fire("Error", "Failed to export report.", "error")
    } finally {
      setIsExporting(false)
    }
  }

  // Render Pagination Helper
  const renderPagination = () => {
    if (totalPages <= 1) return null
    const pages = []
    const maxVisible = 5
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2))
    let end = Math.min(totalPages, start + maxVisible - 1)
    if (end - start < maxVisible - 1) start = Math.max(1, end - maxVisible + 1)
    for (let i = start; i <= end; i++) pages.push(i)

    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <small className="text-muted">
          Showing page <strong>{currentPage}</strong> of <strong>{totalPages}</strong> | Total: <strong>{totalCount}</strong> requests
        </small>
        <ul className="pagination pagination-sm mb-0">
          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
            <button className="page-link" onClick={() => handlePageChange(1)}>«</button>
          </li>
          <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
            <button className="page-link" onClick={() => handlePageChange(currentPage - 1)}>‹</button>
          </li>
          {pages.map(p => (
            <li key={p} className={`page-item ${p === currentPage ? "active" : ""}`}>
              <button className="page-link" onClick={() => handlePageChange(p)}>{p}</button>
            </li>
          ))}
          <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
            <button className="page-link" onClick={() => handlePageChange(currentPage + 1)}>›</button>
          </li>
          <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
            <button className="page-link" onClick={() => handlePageChange(totalPages)}>»</button>
          </li>
        </ul>
      </div>
    )
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb title="" breadcrumbItem="Promotion" />

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <Row className="mb-3">
                    <Col xs={12} className="d-flex justify-content-between align-items-center">
                      <Nav tabs className="nav-tabs-custom">
                        {["Pending", "Approved", "Rejected"].map(tab => (
                          <NavItem key={tab}>
                            <NavLink
                              style={{ cursor: "pointer" }}
                              className={classnames({ active: activeTab === tab })}
                              onClick={() => {
                                setActiveTab(tab)
                                setCurrentPage(1)
                              }}
                            >
                              {tab === "Pending"
                                ? "Request Pending"
                                : tab === "Approved"
                                ? "Request Promoted"
                                : "Request Rejected"}
                              <Badge
                                className="ms-2"
                                color={
                                  tab === "Pending"
                                    ? "warning"
                                    : tab === "Approved"
                                    ? "success"
                                    : "danger"
                                }
                              >
                                {counts[tab]}
                              </Badge>
                            </NavLink>
                          </NavItem>
                        ))}
                      </Nav>
                      <Button
                        color="primary"
                        className="btn btn-primary"
                        onClick={() => navigate("/promoted-student")}
                      >
                        <i className="bx bx-user-check me-1" />
                        Promoted Student Report
                      </Button>
                    </Col>
                  </Row>

                  <Row className="mb-4 align-items-center">
                    <Col sm={6} md={4} className="mb-3 mb-sm-0">
                      <div style={{ width: "240px" }}>
                        <Select
                          value={selectedClass}
                          onChange={setSelectedClass}
                          options={classList}
                          placeholder="Class / Section"
                          styles={selectStyles}
                          isSearchable={true}
                          isClearable={true}
                        />
                      </div>
                    </Col>
                    <Col sm={6} md={8} className="text-sm-end">
                      <div className="d-flex align-items-center justify-content-sm-end justify-content-start gap-2">
                        <div className="position-relative" style={{ width: "260px" }}>
                          <Input
                            type="text"
                            placeholder="Search student or code..."
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            style={{ paddingRight: "30px", borderRadius: "4px" }}
                          />
                          {searchQuery && (
                            <i
                              className="bx bx-x position-absolute"
                              onClick={() => setSearchQuery("")}
                              style={{
                                right: "10px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                cursor: "pointer",
                                fontSize: "18px",
                                color: "#74788d",
                                zIndex: 10,
                              }}
                            />
                          )}
                        </div>
                        <Dropdown
                          isOpen={exportDropdownOpen}
                          toggle={() => setExportDropdownOpen(!exportDropdownOpen)}
                        >
                          <DropdownToggle
                            color="primary"
                            className="btn btn-primary"
                            caret
                            disabled={isExporting}
                          >
                            {isExporting ? (
                              <>
                                <Spinner size="sm" className="me-1" />
                                Exporting...
                              </>
                            ) : (
                              <>
                                <i className="bx bx-download me-1" />
                                Export
                              </>
                            )}
                          </DropdownToggle>
                          <DropdownMenu end>
                            <DropdownItem onClick={() => handleExport("excel")}>
                              <i className="bx bx-file me-2 text-success" />
                              Excel
                            </DropdownItem>
                            <DropdownItem onClick={() => handleExport("pdf")}>
                              <i className="bx bxs-file-pdf me-2 text-danger" />
                              PDF
                            </DropdownItem>
                          </DropdownMenu>
                        </Dropdown>
                      </div>
                    </Col>
                  </Row>

                  <div className="table-responsive">
                    {loading ? (
                      <div className="text-center py-5">
                        <Spinner color="primary" />
                        <p className="mt-2 text-muted">Loading requests…</p>
                      </div>
                    ) : (
                      <Table className="table align-middle table-nowrap table-hover mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>Student</th>
                            <th>Current Class / Sec</th>
                            <th>Next Class / Sec</th>
                            <th>Result Status</th>
                            <th>Fee Status</th>
                            <th>Teacher's Remark</th>
                            {activeTab !== "Pending" && <th>Admin's Remark</th>}
                            <th>Promotion Status</th>
                            {activeTab === "Pending" && <th>Actions</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {requests.length > 0 ? (
                            requests.map(req => (
                              <tr key={req.id}>
                                <td>
                                  <div>
                                    <h5 className="font-size-14 mb-1 text-dark">
                                      {req.st_name || "—"}
                                    </h5>
                                    <p className="text-muted font-size-12 mb-0">
                                      Code: {req.st_code}
                                    </p>
                                  </div>
                                </td>
                                <td>
                                  <span className="font-size-13 text-uppercase">
                                    {req.current_class || "—"} - {req.current_sec || "—"}
                                  </span>
                                </td>
                                <td>
                                  <span className="font-size-13 text-uppercase">
                                    {req.next_class || "—"} - {req.next_sec || "—"}
                                  </span>
                                </td>
                                <td>
                                  <span
                                    style={{
                                      display: "inline-block",
                                      backgroundColor: "#f0f0f0",
                                      color: "#6c757d",
                                      borderRadius: "20px",
                                      padding: "4px 12px",
                                      fontSize: "13px",
                                      fontWeight: 500,
                                    }}
                                  >
                                    {req.result_status || "—"}
                                    {req.overall_average != null && (
                                      <span> ({req.overall_average}%)</span>
                                    )}
                                  </span>
                                </td>
                                <td>
                                  <span
                                    style={{
                                      display: "inline-block",
                                      padding: "6px 14px",
                                      borderRadius: "4px",
                                      fontSize: "13px",
                                      fontWeight: 500,
                                      border: req.fee_status === "Clear" || req.fee_status === "Paid"
                                        ? "1px solid #34c38f"
                                        : "1px solid #f1b44c",
                                      backgroundColor: req.fee_status === "Clear" || req.fee_status === "Paid"
                                        ? "#e6f8f0"
                                        : "#fef8e8",
                                      color: req.fee_status === "Clear" || req.fee_status === "Paid"
                                        ? "#1a7a54"
                                        : "#946c00",
                                    }}
                                  >
                                    {req.fee_status || (req.fee_due_amount === 0 ? "Clear" : "Pending")}
                                    {req.fee_status === "Pending" && req.fee_due_amount != null && (
                                      <span> (₹{req.fee_due_amount})</span>
                                    )}
                                  </span>
                                </td>
                                <td
                                  style={{
                                    maxWidth: "220px",
                                    whiteSpace: "normal",
                                    wordBreak: "break-word",
                                  }}
                                >
                                  <span className="text-muted">{req.teacher_remark || "—"}</span>
                                </td>
                                {activeTab !== "Pending" && (
                                  <td
                                    style={{
                                      maxWidth: "220px",
                                      whiteSpace: "normal",
                                      wordBreak: "break-word",
                                    }}
                                  >
                                    <span className="text-dark font-size-13">
                                      {req.admin_remark || "—"}
                                    </span>
                                  </td>
                                )}
                                <td>
                                  <Badge
                                    color={
                                      req.promotion_status === "Pending Admin Approval"
                                        ? "warning"
                                        : req.promotion_status === "Promoted"
                                        ? "success"
                                        : req.promotion_status === "Approved"
                                        ? "success"
                                        : "danger"
                                    }
                                    className="font-size-12 px-3 py-2"
                                  >
                                    {req.promotion_status === "Pending Admin Approval" ? "Pending" : req.promotion_status}
                                  </Badge>
                                </td>
                                {activeTab === "Pending" && (
                                  <td>
                                    <div className="d-flex gap-2">
                                      <Button
                                        color="success"
                                        size="sm"
                                        onClick={() => openApproveModal(req)}
                                      >
                                        Promote
                                      </Button>
                                      <Button
                                        color="danger"
                                        size="sm"
                                        onClick={() => openRejectModal(req)}
                                      >
                                        Reject
                                      </Button>
                                    </div>
                                  </td>
                                )}
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={9} className="text-center py-4 text-muted">
                                No promotion requests found.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    )}
                  </div>
                  {!loading && renderPagination()}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Approve Confirmation Modal */}
      <Modal isOpen={approveModal} toggle={() => setApproveModal(!approveModal)} centered>
        <ModalHeader toggle={() => setApproveModal(!approveModal)}>
          Confirm Promotion
        </ModalHeader>
        <ModalBody>
          {selectedRequest && (
            <p>
              Are you sure you want to promote the student{" "}
              <strong>{selectedRequest.st_name}</strong> (Code:{" "}
              {selectedRequest.st_code})?
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setApproveModal(false)}>
            Cancel
          </Button>
          <Button color="success" onClick={handleApprove}>
            Confirm Promote
          </Button>
        </ModalFooter>
      </Modal>

      {/* Reject Confirmation Modal */}
      <Modal isOpen={rejectModal} toggle={() => setRejectModal(!rejectModal)} centered>
        <ModalHeader toggle={() => setRejectModal(!rejectModal)}>
          Reject Promotion Request
        </ModalHeader>
        <ModalBody>
          {selectedRequest && (
            <div className="mb-3">
              <p>
                Are you sure you want to reject the promotion request for{" "}
                <strong>{selectedRequest.st_name}</strong> (Code:{" "}
                {selectedRequest.st_code})?
              </p>
              <Label className="form-label mt-2">Rejection Reason / Admin Remark</Label>
              <Input
                type="textarea"
                rows="3"
                value={rejectionReason}
                onChange={e => setRejectionReason(e.target.value)}
                placeholder="Enter reason for rejection (optional)..."
              />
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setRejectModal(false)}>
            Cancel
          </Button>
          <Button color="danger" onClick={handleReject}>
            Confirm Reject
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  )
}

export default PromotionRequests
